import { Component, Input, NgZone, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { ChartConfiguration, ChartData, ChartDataset, Scale } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { Subscription } from 'rxjs';
import {
  KnxWeatherChannel, KnxWeatherConfidence, KnxWeatherQuantity, KnxWeatherSnapshot,
} from '../version.service';
import { updatedAgo as fmtAgo } from '../shared/format.utils';
import {
  CHART_RANGES, CHART_RANGE_MS, ChartRange, chartPalette, coveringRange, dragZoom, relativeDayTick, timeAxisTick, alignedTimeTicks, uiLocale,
} from '../shared/chart.utils';
import { DeepLinkService } from '../shared/deep-link.service';
import { KnxSinceFormatter, PhoneMedia } from './knx-devices.utils';

/** One archived value of a group address ("knxga-…" series; booleans are stored as 0 / 1). */
interface WxPoint { ts: number; value: number; }

/** Display metadata of a quantity: icon, i18n label, default unit (when the backend sends none),
 *  decimals, card order and the chart line colour on the light / dark surface (never the orange of
 *  the dashboard's set-point lines). */
interface QuantityMeta {
  icon:    string;
  label:   string;
  unit:    string;
  digits:  number;   // -1 = automatic (up to two decimals)
  order:   number;
  light:   string;
  dark:    string;
}

const QUANTITIES: Record<KnxWeatherQuantity, QuantityMeta> = {
  outdoorTemperature: { icon: '🌡',  label: 'KNX_WX_OUTDOOR_TEMP', unit: '°C',  digits: 1,  order: 0, light: '#e03131', dark: '#ff6b6b' },
  temperature:        { icon: '🌡',  label: 'KNX_WX_TEMP',         unit: '°C',  digits: 1,  order: 1, light: '#d6336c', dark: '#f06595' },
  humidity:           { icon: '💧', label: 'KNX_WX_HUMIDITY',     unit: '%',   digits: 0,  order: 2, light: '#1098ad', dark: '#3bc9db' },
  windSpeed:          { icon: '💨', label: 'KNX_WX_WIND',         unit: 'm/s', digits: 1,  order: 3, light: '#0ca678', dark: '#38d9a9' },
  windGust:           { icon: '🌬', label: 'KNX_WX_GUST',         unit: 'm/s', digits: 1,  order: 4, light: '#7048e8', dark: '#9775fa' },
  brightness:         { icon: '☀️', label: 'KNX_WX_BRIGHTNESS',   unit: 'lx',  digits: 0,  order: 5, light: '#f59f00', dark: '#ffd43b' },
  pressure:           { icon: '⏲️', label: 'KNX_WX_PRESSURE',     unit: 'hPa', digits: 0,  order: 6, light: '#495057', dark: '#ced4da' },
  percent:            { icon: '📐', label: 'KNX_WX_PERCENT',      unit: '%',   digits: 0,  order: 7, light: '#5c7cfa', dark: '#91a7ff' },
  rain:               { icon: '🌧', label: 'KNX_WX_RAIN',         unit: '',    digits: 1,  order: 7, light: '#1c7ed6', dark: '#4dabf7' },
  binary:             { icon: '🔘', label: 'KNX_WX_BINARY',       unit: '',    digits: 0,  order: 8, light: '#1c7ed6', dark: '#4dabf7' },
  unknown:            { icon: '❔', label: 'KNX_WX_UNKNOWN',      unit: '',    digits: -1, order: 9, light: '#868e96', dark: '#adb5bd' },
};

/** One channel card (view model, rebuilt per snapshot and language switch). */
export interface WxCard {
  ga:         string;
  series:     string;
  /** Deep-link view path of the card's popup, also its chart's link id: "knx/weather/knxga-8-0-5". */
  linkId:     string;
  quantity:   KnxWeatherQuantity;
  meta:       QuantityMeta;
  confidence: KnxWeatherConfidence;
  /** i18n key of the confidence hint (badge tooltip); null for 'book'. */
  hintKey:    string | null;
  name:       string | null;
  dpt:        string | null;
  /** 1-bit channel: state pill + last change instead of a number. */
  binary:     boolean;
  /** Display unit and factor (Pa → hPa); the archive keeps the raw value. */
  unit:       string;
  scale:      number;
  digits:     number;
  /** Big value ("19,1") and its unit ("°C" / "klx"); null when no value arrived yet. */
  valueNum:   string | null;
  valueUnit:  string;
  on:         boolean | null;   // binary channels
  ts:         number | null;
  /** Last change of the value (backend, seeded from 30 days of archive); null = not known. */
  changedAt:  number | null;
  /** Last telegram, "18:42" / "Mo. 18:42" (shown for binary channels only when no change time is known). */
  tsText:     string;
  /** min / max of the last 24 h from the backend (raw units), null → computed from the sparkline. */
  min24h:     number | null;
  max24h:     number | null;
  /** Latest numeric value (booleans as 0 / 1), appended to the live 24 h chart. */
  live:       number | null;
}

/** Card footer: formatted min / max (numeric) or the on-share and last change (binary). */
interface WxFooter { min: string; max: string; since: string | null; sinceFull: string | null; onPct: number | null; }

const DAY_MS = CHART_RANGE_MS.day;
/** Sparklines are re-read from the archive at most this often while the page is visible. */
const REFRESH_MS = 5 * 60 * 1000;
/** Archive reads in parallel (one GET per channel). */
const CONCURRENCY = 3;
/** Skeletons until the first "knxWeather" frame; afterwards (or without any frame) the empty state. */
const WAIT_MS = 8000;
const NO_POINTS: WxPoint[] = Object.freeze([]) as unknown as WxPoint[];

/**
 * KNX weather station (read-only, passively overheard): one card per group address the station
 * sends, with the backend's quantity classification and honest confidence badge, the live value,
 * 24 h min / max and a 24 h sparkline from the throttled GA archive (/api/history?series=knxga-…).
 * Tap → larger chart with Tag / Woche / Monat / Jahr.
 */
@Component({
  selector: 'app-knx-weather',
  templateUrl: './knx-weather.component.html',
  styles: [`
    :host { display: block; }
    .wx-icon   { font-size: 1rem; line-height: 1; flex-shrink: 0; }
    /* Long German labels ("Außentemperatur") hyphenate (lang set on the element) instead of breaking mid-word;
       the badge drops below the label when both do not fit (header row wraps). */
    .wx-head   { flex-wrap: wrap; row-gap: .15rem; }
    .wx-label  { font-size: .7rem; font-weight: 600; letter-spacing: .03em; text-transform: uppercase; line-height: 1.25; min-width: 0;
                 flex: 1 1 auto; overflow-wrap: break-word; hyphens: auto; -webkit-hyphens: auto; padding-top: .05rem; }
    .wx-sub    { font-size: .66rem; line-height: 1.25; color: var(--bs-secondary-color); }
    .wx-badge  { flex-shrink: 0; font-size: .58rem; font-weight: 600; line-height: 1; padding: .18rem .35rem; border-radius: 999px;
                 border: 1px solid var(--bs-border-color); color: var(--bs-secondary-color); white-space: nowrap; }
    .wx-badge-unsure { border-style: dashed; }
    .wx-value  { font-size: 1.45rem; font-weight: 600; line-height: 1.15; font-variant-numeric: tabular-nums; white-space: nowrap; }
    .wx-unit   { font-size: .8rem; font-weight: 500; color: var(--bs-secondary-color); margin-left: .2rem; }
    .wx-pill   { display: inline-flex; align-items: center; justify-content: center; min-width: 3.3rem; padding: .16rem .5rem;
                 border-radius: 999px; font-size: .72rem; font-weight: 700; letter-spacing: .05em; line-height: 1.2;
                 color: var(--bs-secondary-color); border: 1px solid var(--bs-border-color); }
    .wx-pill-on { color: #fff; background: var(--bs-primary); border-color: var(--bs-primary); }
    .wx-since  { font-size: .66rem; line-height: 1.2; color: var(--bs-secondary-color); }
    .wx-spark  { position: relative; height: 56px; margin-top: 4px; }
    .wx-foot   { font-size: .6rem; line-height: 1; margin-top: 2px; color: var(--bs-secondary-color); font-variant-numeric: tabular-nums; }
    .wx-big    { position: relative; aspect-ratio: 4 / 3; max-height: 70vh; }
    .wx-nodata { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; pointer-events: none; }
    .wx-flash  { border-radius: var(--bs-card-border-radius, .375rem); }
    .wx-pop-info { font-size: .7rem; line-height: 1.4; color: var(--bs-secondary-color); }
    .wx-pop-meta { font-size: .6rem; }
    @media (min-width: 576px) {
      .wx-value { font-size: 1.7rem; }
      .wx-spark { height: 64px; }
      .wx-big   { aspect-ratio: 16 / 9; }
    }
    /* Mobile view (< 768 px, landscape phones included): no text under 11 px */
    @media (max-width: 767.98px) {
      .wx-sub, .wx-since, .wx-foot, .wx-badge, .wx-pop-meta { font-size: .6875rem; }
    }
    /* Mobile view from 576 px: the popup's range buttons in the phones' touch size (styles.css sizes .dash-seg
       only below 576 px) */
    @media (min-width: 576px) and (max-width: 767.98px) {
      .dash-seg > .btn { min-height: var(--dash-touch-sm); padding: 4px 12px; font-size: .8125rem; }
      .dash-seg > .btn-outline-secondary { --bs-btn-color: var(--bs-secondary-color); }
    }
    /* Phones: the station line below the title; in the popup a full-width range control and a chart filling
       most of the sheet */
    @media (max-width: 575.98px) {
      .wx-title   { flex-wrap: wrap; row-gap: .1rem; }
      .wx-station { flex-basis: 100%; white-space: normal; }
      .wx-sub .wx-badge { margin-left: .3rem; padding: .1rem .35rem; vertical-align: .05rem; }
      .wx-pop-info { flex: 1 0 100%; font-size: .8125rem; }
      .wx-pop-ctl  { flex: 1 0 100%; margin-left: 0 !important; }
      .wx-pop-ctl > .dash-seg { flex: 1 1 auto; width: auto; }
      .wx-big      { aspect-ratio: auto; height: var(--dash-sheet-chart-h); max-height: none; }
    }
  `],
})
export class KnxWeatherComponent implements OnChanges, OnDestroy {
  /** Latest "knxWeather" frame; null until one arrived. */
  @Input() weather:   KnxWeatherSnapshot | null = null;
  @Input() liveDotOn = true;
  @Input() nowTick   = Date.now();
  @Input() darkMode  = false;

  cards: WxCard[] = [];
  /** Station line under the title ("1.3.10 · Linie 1.3"), null when none is known. */
  station: { device: string; line: string | null } | null = null;
  /** Shown while no frame arrived yet (and not longer than WAIT_MS). */
  waiting = true;

  /** Restarts a card's highlight when a new telegram for it arrived. */
  flash: Record<string, boolean> = {};

  // ── 24 h sparklines (archive, refreshed every 5 min while visible, plus live appends) ──
  private day = new Map<string, WxPoint[]>();
  sparkData:    Record<string, ChartData<'line'>> = {};
  sparkOptions: Record<string, ChartConfiguration<'line'>['options']> = {};
  private dayAbort: AbortController | null = null;
  /** Running loadDay() batches (a channel showing up mid-refresh starts a second one). */
  private dayPending = 0;
  private dayFetchedAt = 0;
  /** Per card: the footer and what it was computed from (the template asks on every change detection). */
  private footers = new Map<string, { pts: WxPoint[]; key: string; f: WxFooter }>();

  // ── Popup: one open channel, its range and chart ──
  readonly chartRanges = CHART_RANGES;
  range: ChartRange = 'day';
  rangeLoading = false;
  openGa: string | null = null;
  private rangePoints = new Map<string, WxPoint[]>();
  private rangeAbort: AbortController | null = null;
  expandedData:    ChartData<'line'> = { datasets: [] };
  expandedOptions: ChartConfiguration<'line'>['options'] = {};
  expandedCount = 0;

  private lastTs = new Map<string, number>();
  private flashTimers = new Map<string, any>();
  private waitTimer: any;
  private refreshTimer: any;
  private numFmts = new Map<string, Intl.NumberFormat>();
  /** Phones: the popup chart gets fewer ticks (options rebuilt when the width crosses 576 px). */
  private readonly phone = new PhoneMedia(() => this.buildExpanded());
  lang: string;
  private langSub: Subscription;
  private linkSub: Subscription;

  constructor(readonly translate: TranslateService, private readonly link: DeepLinkService, private readonly zone: NgZone) {
    this.lang = translate.currentLang || translate.defaultLang || 'en';
    // Numbers, "since" times and the chart axes are formatted in TS: rebuild on a language switch.
    this.langSub = translate.onLangChange.subscribe((e: LangChangeEvent) => {
      this.lang = e.lang;
      this.numFmts.clear();
      this.buildCards();
      for (const c of this.cards) this.buildSpark(c);
      this.buildExpanded();
    });
    // Back / forward or an edited link (the popups open and close themselves via app-popup-card [linkId])
    this.linkSub = link.external$.subscribe(() => this.followLink());
    this.waitTimer = setTimeout(() => { this.waiting = false; }, WAIT_MS);
    this.refreshTimer = setInterval(() => this.refreshIfDue(), 60 * 1000);
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['weather']) {
      if (this.weather) { this.waiting = false; clearTimeout(this.waitTimer); }
      this.buildCards();
      this.applyLive();
      // Channels not read yet (first frame, or a channel that showed up later).
      const missing = this.cards.map(c => c.series).filter(s => !this.day.has(s));
      if (missing.length) this.loadDay(missing);
    }
    if (changes['darkMode'] && !changes['darkMode'].firstChange) {
      for (const c of this.cards) this.buildSpark(c);
      this.buildExpanded();
    }
  }

  ngOnDestroy(): void {
    this.langSub.unsubscribe();
    this.linkSub.unsubscribe();
    clearTimeout(this.waitTimer);
    clearInterval(this.refreshTimer);
    for (const t of this.flashTimers.values()) clearTimeout(t);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.dayAbort?.abort();
    this.rangeAbort?.abort();
    this.phone.destroy();
  }

  trackCard = (_: number, c: WxCard) => c.ga;

  updatedAgo(ts: number | null): string { return fmtAgo(ts, this.nowTick); }

  // ── Cards ───────────────────────────────────────────────────────────────────
  private buildCards(): void {
    const w = this.weather;
    const known = !!w?.device && w.channels.length > 0;
    this.station = w?.device ? { device: w.device, line: w.line ?? KnxWeatherComponent.lineOf(w.device) } : null;
    if (!known) { this.cards = []; return; }
    const fmt = new KnxSinceFormatter();
    this.cards = w!.channels
      .map(ch => this.card(ch, fmt))
      .sort((a, b) => a.meta.order - b.meta.order || KnxWeatherComponent.gaCompare(a.ga, b.ga));
  }

  private card(ch: KnxWeatherChannel, fmt: KnxSinceFormatter): WxCard {
    const quantity: KnxWeatherQuantity = Object.prototype.hasOwnProperty.call(QUANTITIES, ch.quantity) ? ch.quantity : 'unknown';
    const meta = QUANTITIES[quantity];
    // 1-bit: a boolean value or a DPT 1.x; without any value yet, the binary quantities themselves.
    const binary = typeof ch.value === 'boolean' || /^1(\.|$)/.test(ch.dpt ?? '')
      || (ch.value == null && (quantity === 'binary' || quantity === 'rain'));
    let unit = binary ? '' : (ch.unit ?? meta.unit);
    let scale = 1;
    // Pressure in Pa (DPT 9.006) reads better in hPa.
    if (quantity === 'pressure' && (unit === 'Pa' || (typeof ch.value === 'number' && Math.abs(ch.value) > 2000))) {
      unit = 'hPa';
      scale = 0.01;
    }
    const live = typeof ch.value === 'boolean' ? (ch.value ? 1 : 0)
      : typeof ch.value === 'number' && Number.isFinite(ch.value) ? ch.value : null;
    const c: WxCard = {
      ga: ch.ga,
      series: ch.series,
      linkId: `knx/weather/${ch.series}`,
      quantity, meta,
      confidence: ch.confidence,
      hintKey: ch.confidence === 'likely' ? 'KNX_WX_LIKELY_HINT' : ch.confidence === 'unsure' ? 'KNX_WX_UNSURE_HINT' : null,
      name: ch.name ?? null,
      dpt: ch.dpt ?? null,
      binary, unit, scale,
      digits: meta.digits,
      valueNum: null,
      valueUnit: unit,
      on: binary && live != null ? live >= 0.5 : null,
      ts: ch.ts,
      changedAt: ch.changedAt ?? null,
      tsText: ch.ts != null ? fmt.short(ch.ts) : '',
      min24h: ch.min24h ?? null,
      max24h: ch.max24h ?? null,
      live,
    };
    if (!binary && live != null) {
      const v = this.split(c, live);
      c.valueNum = v.num;
      c.valueUnit = v.unit;
    } else if (!binary && ch.text) {
      c.valueNum = ch.text;   // non-numeric value: the backend's text as is
    }
    return c;
  }

  /** New telegrams: flash their card and append the value to the live 24 h chart. */
  private applyLive(): void {
    for (const c of this.cards) {
      if (c.ts == null) continue;
      const prev = this.lastTs.get(c.ga);
      this.lastTs.set(c.ga, c.ts);
      if (prev == null || c.ts <= prev) continue;
      this.flashCard(c.ga);
      const pts = this.day.get(c.series);
      if (!pts || c.live == null) continue;
      const merged = this.withLive(c, pts);
      if (merged === pts) continue;
      this.day.set(c.series, merged);
      this.buildSpark(c);
      if (this.openGa === c.ga && this.range === 'day') this.buildExpanded();
    }
  }

  /** The card's live value appended when it is newer than the last archived point (window trimmed to 24 h). */
  private withLive(c: WxCard, pts: WxPoint[]): WxPoint[] {
    if (c.live == null || c.ts == null) return pts;
    const last = pts[pts.length - 1];
    if (last && c.ts <= last.ts) return pts;
    const cutoff = Date.now() - DAY_MS;
    const out = pts[0]?.ts < cutoff ? pts.filter(p => p.ts >= cutoff) : pts.slice();
    out.push({ ts: c.ts, value: c.live });
    return out;
  }

  private flashCard(ga: string): void {
    clearTimeout(this.flashTimers.get(ga));
    this.flash[ga] = false;
    this.flashTimers.set(ga, setTimeout(() => {
      this.flash[ga] = true;
      this.flashTimers.set(ga, setTimeout(() => { this.flash[ga] = false; }, 900));
    }, 0));
  }

  /** Footer of a card: 24 h min / max (backend, else from the sparkline) or on-share + last change. */
  footer(c: WxCard): WxFooter {
    const pts = this.day.get(c.series) ?? NO_POINTS;
    const key = `${this.lang}|${c.min24h}|${c.max24h}|${c.on}|${c.scale}|${c.unit}|${c.ts}|${c.changedAt}`;
    const hit = this.footers.get(c.ga);
    if (hit && hit.pts === pts && hit.key === key) return hit.f;
    let f: WxFooter;
    if (c.binary) {
      f = { min: '', max: '', onPct: KnxWeatherComponent.onShare(pts, Date.now()), ...this.lastChange(c, pts) };
    } else {
      let min = c.min24h, max = c.max24h;
      if ((min == null || max == null) && pts.length) {
        min = Infinity; max = -Infinity;
        for (const p of pts) { if (p.value < min) min = p.value; if (p.value > max) max = p.value; }
      }
      f = {
        min: min != null && Number.isFinite(min) ? this.fmt(c, min) : '',
        max: max != null && Number.isFinite(max) ? this.fmt(c, max) : '',
        since: null, sinceFull: null, onPct: null,
      };
    }
    this.footers.set(c.ga, { pts, key, f });
    return f;
  }

  /**
   * Share (%) of the time a 1-bit channel was on, over the span its points cover (first point → now;
   * null without any span). Time-weighted, each value holding until the next one: the archive writes
   * such a GA on change (plus a heartbeat only while it keeps sending), so a plain mean of the points
   * would read "on 19:31, off 20:17, off now" as 33 %. A downsampled bucket (0…1) counts with its share.
   */
  private static onShare(pts: WxPoint[], now: number): number | null {
    let on = 0, total = 0;
    for (let i = 0; i < pts.length; i++) {
      const end = i + 1 < pts.length ? pts[i + 1].ts : Math.max(now, pts[i].ts);
      const d = end - pts[i].ts;
      if (!(d > 0)) continue;
      on += d * Math.min(1, Math.max(0, pts[i].value));
      total += d;
    }
    return total > 0 ? Math.round(100 * on / total) : null;
  }

  /** Since when the current state holds: the backend's exact last change (it knows 30 days of archive),
   *  else the start of the current run in the 24 h points (bucket resolution ≈ 5 min). */
  private lastChange(c: WxCard, pts: WxPoint[]): { since: string | null; sinceFull: string | null } {
    if (c.on == null) return { since: null, sinceFull: null };
    let ts: number | null = c.changedAt;
    if (ts == null && pts.length) {
      const state = c.on ? 1 : 0;
      let i = pts.length - 1;
      while (i >= 0 && Math.round(pts[i].value) === state) i--;
      if (i >= 0) ts = pts[i + 1]?.ts ?? c.ts;   // i < 0: unchanged for the whole window
    }
    if (ts == null) return { since: null, sinceFull: null };
    const fmt = new KnxSinceFormatter();
    return { since: fmt.short(ts), sinceFull: fmt.long(ts) };
  }

  // ── Formatting ──────────────────────────────────────────────────────────────
  private numFmt(digits: number): Intl.NumberFormat {
    const locale = uiLocale();
    const key = `${locale}|${digits}`;
    let f = this.numFmts.get(key);
    if (!f) {
      f = digits < 0
        ? new Intl.NumberFormat(locale, { maximumFractionDigits: 2 })
        : new Intl.NumberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits });
      this.numFmts.set(key, f);
    }
    return f;
  }

  /** Brightness from 10 000 lx on reads in klx (`v` in display units). */
  private static kilo(c: WxCard, v: number): boolean {
    return c.quantity === 'brightness' && c.unit === 'lx' && Math.abs(v) >= 10000;
  }

  /** Raw value → display number + unit (hPa scaling; brightness from 10 000 lx in klx). */
  private split(c: WxCard, raw: number): { num: string; unit: string } {
    let v = raw * c.scale;
    let unit = c.unit;
    let digits = c.digits;
    if (KnxWeatherComponent.kilo(c, v)) {
      v /= 1000;
      unit = 'klx';
      digits = Math.abs(v) >= 100 ? 0 : 1;
    }
    return { num: this.numFmt(digits).format(v), unit };
  }

  /** "19,1 °C" / "54 %" / "AN" (binary). */
  fmt(c: WxCard, raw: number): string {
    if (c.binary) return this.translate.instant(raw >= 0.5 ? 'KNX_ON' : 'KNX_OFF');
    const v = this.split(c, raw);
    return v.unit ? `${v.num} ${v.unit}` : v.num;
  }

  /**
   * y axis tick: one unit along the whole axis (klx once it reaches 10 klx, not "5.000 lx" below "10,0 klx")
   * and at least the decimals the tick step needs, so neighbouring ticks never print the same text.
   */
  private tickLabel(c: WxCard, raw: number, ticks: { value: number }[]): string {
    if (c.binary || c.digits < 0) return this.fmt(c, raw);
    let v = raw * c.scale;
    let step = Math.abs((ticks[1]?.value ?? 0) - (ticks[0]?.value ?? 0)) * c.scale;
    let unit = c.unit;
    let digits = c.digits;
    if (KnxWeatherComponent.kilo(c, Math.max(0, ...ticks.map(t => Math.abs(t.value))) * c.scale)) {
      v /= 1000;
      step /= 1000;
      unit = 'klx';
      digits = 0;
    }
    const num = this.numFmt(Math.max(digits, KnxWeatherComponent.stepDigits(step))).format(v);
    return unit ? `${num} ${unit}` : num;
  }

  /**
   * Display precision of a channel in raw (archive) units — the y axis' `resolution`, whose 10 steps are
   * its minimum span: 0,1 °C, 1 %, 0,1 m/s, 1 lx (0,1 / 1 klx from 10 / 100 klx), 1 hPa (= 100 Pa),
   * 0,01 for the automatic decimals of an unknown quantity.
   */
  private static resolution(c: WxCard): number {
    if (c.quantity === 'brightness' && c.unit === 'lx' && c.live != null && Math.abs(c.live) >= 10000) {
      return Math.abs(c.live) >= 100000 ? 1000 : 100;
    }
    return (c.digits < 0 ? 0.01 : Math.pow(10, -c.digits)) / c.scale;
  }

  /** Decimals an axis needs so neighbouring ticks never print the same text (step 0.5 → 1, 0.02 → 2). */
  private static stepDigits(step: number): number {
    return step > 0 && step < 1 ? Math.min(3, Math.ceil(-Math.log10(step) - 1e-9)) : 0;
  }

  /**
   * The y axis' 8 % headroom (Chart.js' `grace: '8%'`, added after the chart plugin's minimum span), kept at
   * or above zero when the data is not negative — `grace` pushed a 0 m/s calm or a dark night below zero and
   * the nice ticks made that −2 m/s / −50 klx; temperatures below zero keep it — and at or below `cap` (100 %)
   * when the data stays there (else the ticks ran up to 150 %), the window shifted down to keep its span.
   */
  private static headroom(scale: Scale, cap: number | null): void {
    const { min, max } = scale;
    if (!Number.isFinite(min) || !Number.isFinite(max)) return;
    const data = scale.getMinMax(false);   // the data's own range (the minimum span may have widened min / max)
    const pad = 0.04 * (max - min);
    let lo = min - pad, hi = max + pad;
    if (cap != null && data.max <= cap && hi > cap) { lo -= hi - cap; hi = cap; }
    if (data.min >= 0 && lo < 0) lo = 0;
    scale.min = lo;
    scale.max = hi;
  }

  private color(c: WxCard): string {
    return this.darkMode ? c.meta.dark : c.meta.light;
  }

  private static lineOf(device: string): string | null {
    const parts = device.split('.');
    return parts.length === 3 ? `${parts[0]}.${parts[1]}` : null;
  }

  private static gaCompare(a: string, b: string): number {
    const pa = a.split('/').map(Number), pb = b.split('/').map(Number);
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
      const d = (pa[i] ?? 0) - (pb[i] ?? 0);
      if (d) return d;
    }
    return 0;
  }

  // ── Archive reads ───────────────────────────────────────────────────────────
  private static fetchPoints(series: string, from: number, to: number, buckets: number, signal: AbortSignal): Promise<WxPoint[]> {
    return fetch(`/api/history?series=${encodeURIComponent(series)}&from=${from}&to=${to}&buckets=${buckets}`, { signal })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((raw: unknown) => {
        if (!Array.isArray(raw)) return NO_POINTS;
        const pts: WxPoint[] = [];
        for (const p of raw) {
          const v = typeof p?.value === 'boolean' ? (p.value ? 1 : 0) : p?.value;
          if (typeof p?.ts === 'number' && typeof v === 'number' && Number.isFinite(v)) pts.push({ ts: p.ts, value: v });
        }
        return pts.sort((a, b) => a.ts - b.ts);
      });
  }

  /** Reads the 24 h sparklines of these series (CONCURRENCY at a time); a failed read leaves an empty chart until the next refresh. */
  private loadDay(series: string[]): void {
    if (!series.length) return;
    this.dayAbort ??= new AbortController();
    const signal = this.dayAbort.signal;
    const queue = [...new Set(series)];
    // Placeholder, so a frame arriving meanwhile does not queue the same series again.
    for (const s of queue) if (!this.day.has(s)) this.day.set(s, NO_POINTS);
    this.dayPending++;
    this.dayFetchedAt = Date.now();
    const to = Date.now(), from = to - DAY_MS;
    const worker = async (): Promise<void> => {
      for (let s = queue.shift(); s !== undefined; s = queue.shift()) {
        const pts = await KnxWeatherComponent.fetchPoints(s, from, to, 300, signal).catch(() => null);
        if (signal.aborted) return;
        if (!pts) continue;
        const card = this.cards.find(c => c.series === s);
        this.day.set(s, card ? this.withLive(card, pts) : pts);
        if (card) {
          this.buildSpark(card);
          if (this.openGa === card.ga && this.range === 'day') this.buildExpanded();
        }
      }
    };
    Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker))
      .finally(() => { this.dayPending--; });
  }

  private refreshIfDue(): void {
    if (document.visibilityState !== 'visible' || this.dayPending > 0 || !this.cards.length) return;
    if (Date.now() - this.dayFetchedAt < REFRESH_MS) return;
    this.loadDay(this.cards.map(c => c.series));
  }

  private onVisibility = (): void => this.refreshIfDue();

  // ── Popup ───────────────────────────────────────────────────────────────────
  /**
   * A card's popup opened / closed (a tap, or app-popup-card following the deep link). Its link view:
   * "#knx/weather/<series>?range=week&from=…&to=…" — range and zoom window of its chart, unprefixed.
   */
  onPopup(c: WxCard, open: boolean): void {
    if (!open) {
      if (this.openGa === c.ga) { this.openGa = null; this.rangeAbort?.abort(); this.rangeAbort = null; this.rangeLoading = false; }
      return;
    }
    if (!this.link.isOpen(c.linkId)) {
      // Opened by a tap: the view follows (app-popup-card opens it right after this), with the range
      // all channel popups share. Opened by a link: the link's range and zoom window apply.
      this.link.openView(c.linkId);
      this.link.setRange(c.linkId, this.range);
    }
    this.openGa = c.ga;
    this.applyRange(this.linkedRange(c));
  }

  /** Range button. */
  setRange(range: ChartRange): void {
    if (range === this.range) return;
    const c = this.openCard;
    if (c) this.link.setRange(c.linkId, range);   // a new range drops the zoom window
    this.applyRange(range);
  }

  /** Shows the open popup's chart for `range` (fetching its points if needed). */
  private applyRange(range: ChartRange): void {
    if (range !== this.range) {
      this.range = range;
      this.rangeAbort?.abort();
      this.rangeAbort = null;
      this.rangePoints.clear();
      this.rangeLoading = false;
    }
    this.ensureRange();
    this.buildExpanded();
  }

  /**
   * The range the link asks for, widened so a linked zoom window lies inside the loaded points (a link
   * opened days later still shows its window); the widened range is written back with the window kept.
   * A window older than the longest range would show nothing: it is dropped.
   */
  private linkedRange(c: WxCard): ChartRange {
    const linked = this.link.range(c.linkId) ?? 'day';
    const zoom = this.link.zoom(c.linkId);
    if (zoom && zoom.to <= Date.now() - CHART_RANGE_MS.year) {
      this.link.setZoom(c.linkId, null);
      return linked;
    }
    const range = coveringRange(linked, zoom);
    if (range !== linked) {
      this.link.setRange(c.linkId, range);   // drops the window …
      this.link.setZoom(c.linkId, zoom);     // … which still applies
    }
    return range;
  }

  /** The browser navigated with this popup still open: its range may have changed (the zoom window is
   *  restored by the chart plugin; another popup opens / this one closes via app-popup-card). */
  private followLink(): void {
    const c = this.openCard;
    if (!c || !this.link.isOpen(c.linkId)) return;
    const range = this.linkedRange(c);
    if (range !== this.range) this.applyRange(range);
  }

  /** The open chart is zoomed in: its window is in the deep link (written by the chart plugin). */
  get zoomed(): boolean {
    const c = this.openCard;
    return !!c && this.link.zoom(c.linkId) != null;
  }

  resetZoom(chart: BaseChartDirective): void {
    (chart?.chart as any)?.resetZoom?.();   // → onZoomComplete: the link drops the window
    const c = this.openCard;
    if (c) this.link.setZoom(c.linkId, null);
    this.buildExpanded();   // the whole range again, up to now
  }

  /** The popup's points are still on their way (archive read of its range, or the first sparkline read). */
  get expandedLoading(): boolean {
    return this.rangeLoading || (this.range === 'day' && this.dayPending > 0 && !this.expandedCount);
  }

  get openCard(): WxCard | null {
    return this.openGa ? this.cards.find(c => c.ga === this.openGa) ?? null : null;
  }

  /** Week / month / year of the open channel: one archive read (the day range is the sparkline's data). */
  private ensureRange(): void {
    const c = this.openCard;
    if (!c || this.range === 'day' || this.rangePoints.has(c.series)) return;
    const ctrl = this.rangeAbort ??= new AbortController();
    const to = Date.now(), from = to - CHART_RANGE_MS[this.range];
    const series = c.series;
    this.rangeLoading = true;
    KnxWeatherComponent.fetchPoints(series, from, to, 600, ctrl.signal)
      .catch(() => NO_POINTS)
      .then(pts => {
        if (ctrl !== this.rangeAbort) return;   // range switched or popup closed meanwhile
        this.rangePoints.set(series, pts);
        this.rangeLoading = false;
        this.buildExpanded();
      });
  }

  // ── Charts ──────────────────────────────────────────────────────────────────
  private buildSpark(c: WxCard): void {
    const pts = this.day.get(c.series) ?? NO_POINTS;
    this.sparkData[c.series] = this.chartData(c, pts, false);
    this.sparkOptions[c.series] = this.chartOptions(c, DAY_MS, false);
  }

  /**
   * Rebuilds the popup chart (new points, range, language, theme). ng2-charts copies the fresh options onto
   * the chart, so x min / max are the whole range again — a zoom window survives anyway: it is in the deep
   * link, and the chart plugin re-applies it (live telegrams keep arriving while zoomed in).
   */
  private buildExpanded(): void {
    const c = this.openCard;
    if (!c) return;
    const pts = (this.range === 'day' ? this.day.get(c.series) : this.rangePoints.get(c.series)) ?? NO_POINTS;
    this.expandedCount = pts.length;
    this.expandedData = this.chartData(c, pts, true);
    this.expandedOptions = this.chartOptions(c, CHART_RANGE_MS[this.range], true);
  }

  private chartData(c: WxCard, pts: WxPoint[], expanded: boolean): ChartData<'line'> {
    const color = this.color(c);
    const data = pts.map(p => ({ x: p.ts, y: p.value }));
    if (c.binary && data.length) {
      // A state holds until the next telegram: carry the last one up to now.
      data.push({ x: Date.now(), y: data[data.length - 1].y });
    }
    return {
      datasets: [{
        linkKey: 'value',   // the deep link's dataset key (no legend: never hidden, but stable)
        data,
        borderColor: color,
        backgroundColor: c.binary ? color + '33' : 'transparent',
        fill: c.binary ? 'origin' : false,
        stepped: c.binary ? 'before' : false,   // a telegram's state holds from its own time ('after' draws the next one's)
        tension: c.binary ? 0 : 0.3,
        borderWidth: expanded ? 2 : 1.5,
        pointRadius: 0,
        pointHoverRadius: 4,
      } as ChartDataset<'line'>],
    };
  }

  private chartOptions(c: WxCard, windowMs: number, expanded: boolean): ChartConfiguration<'line'>['options'] {
    const { textColor, gridColor } = chartPalette(this.darkMode);
    const xMax = Date.now();
    const xMin = xMax - windowMs;
    const font = { size: expanded ? 11 : 10 };
    const long = windowMs > 2 * DAY_MS;
    const title = (items: any[]): string => {
      const ts = items[0]?.parsed.x;
      if (ts == null) return '';
      return new Date(ts).toLocaleString(uiLocale(), long
        ? { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }
        : { hour: '2-digit', minute: '2-digit' });
    };
    // x axis: the range's window (relative ticks for 24 h, dates beyond); zoomed in (a drag or a linked
    // window), ticks on whole hours / days of the visible span, formatted for that span.
    const windowTick = long ? timeAxisTick(xMin, xMax) : relativeDayTick;
    const zoomedIn = (scale: any): boolean => scale.max - scale.min < (xMax - xMin) * 0.98;
    const pad = (n: number) => String(n).padStart(2, '0');
    const label = (item: any): string => {
      const y = item.parsed.y ?? 0;
      // Binary buckets average 0 / 1: anything between is the share of the bucket the signal was on.
      if (c.binary && y > 0.02 && y < 0.98) return ` ${this.translate.instant('KNX_WX_ON_SHARE', { pct: Math.round(y * 100) })}`;
      return ` ${this.fmt(c, y)}`;
    };
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'index', intersect: false },
      layout: { padding: { top: 4, right: expanded ? 6 : 2, bottom: 0, left: 0 } },
      plugins: {
        legend: { display: false },
        tooltip: { displayColors: false, callbacks: { title, label } },
        // The popup chart: zoom window in the deep link (its id = the popup's view path). Chart.js handles
        // the drag outside Angular: run change detection so the Reset button (zoomed) follows at once.
        ...(expanded ? { zoom: dragZoom(() => this.zone.run(() => {})), deepLink: { id: c.linkId } } : {}),
      },
      scales: {
        x: {
          type: 'linear',
          display: expanded,
          min: xMin,
          max: xMax,
          afterBuildTicks: (scale: any) => {
            const aligned = zoomedIn(scale) ? alignedTimeTicks(scale.min, scale.max) : [];
            if (aligned.length >= 2) scale.ticks = aligned.map(value => ({ value }));
          },
          ticks: {
            color: textColor,
            font,
            maxRotation: 0,
            maxTicksLimit: expanded && this.phone.matches ? 4 : 8,
            callback: function (this: any, val: number | string) {
              if (!zoomedIn(this)) return windowTick(val);
              if (this.max - this.min > 2 * DAY_MS) return timeAxisTick(this.min, this.max)(val);
              const d = new Date(val as number);
              return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
            },
          },
          grid: { display: expanded, color: gridColor },
        },
        y: {
          display: expanded,
          // numeric: at least 10 display steps high (chart plugin), so 17,8 ↔ 17,9 °C noise stays flat, plus
          // 8 % headroom that never reaches below zero for data that is not negative
          ...(c.binary ? { min: 0, max: 1 } : {
            resolution: KnxWeatherComponent.resolution(c),
            afterDataLimits: (scale: Scale) => KnxWeatherComponent.headroom(scale, c.unit === '%' ? 100 / c.scale : null),
          }),
          ticks: {
            color: textColor,
            font,
            maxTicksLimit: c.binary ? 2 : 6,
            ...(c.binary ? { stepSize: 1 } : {}),
            callback: (v, _i, ticks) => this.tickLabel(c, Number(v), ticks),
          },
          grid: { display: expanded, color: gridColor },
        },
      },
    };
  }
}
